import {Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {CommonService} from '../../../../../shared/services/common.service';
import * as Validation from '../../../../../shared/constants/validation.constants';
import {ActivatedRoute} from '@angular/router';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import {RegisterRequest, VerifyUsernameRequest} from '../../../../core/authentication.actions';
import {Subject, takeUntil} from 'rxjs';
import {roles, rolesLabelMap} from '../../../../../shared/constants/common.constants';
import {isUsernameAvailable, registrationInfo} from '../../../../core/authentication.selectors';
import * as moment from 'moment';
import {DropdownOption} from '../../../../../../models/dropdown-option.model';
import {FetchGenderFilterRequest} from '../../../../../shared/core/shared.actions';
import {genderInfo} from '../../../../../shared/core/shared.selectors';

@Component({
  selector: 'app-step-one-right-panel',
  templateUrl: './step-one-right-panel.component.html',
  styleUrls: ['./step-one-right-panel.component.css']
})
export class StepOneRightPanelComponent implements OnInit, OnDestroy {
  protected readonly roles = roles;
  protected readonly rolesLabelMap = rolesLabelMap;
  private readonly onDestroy: Subject<any> = new Subject<any>();
  avatarId: any;
  verifyUsername: boolean = false;
  enableForm: boolean = true;
  userRole: number;
  errorMessages: any = {};
  validation = Validation;
  isUsernameAvailable: boolean = true;
  usernameStatusLoading: boolean = false;
  registerLoading: boolean = false;
  verificationStatusIcon = '';
  displayMessage: boolean = false;
  minDobDate: any;
  maxDobDate: any;
  dropdownOptions: DropdownOption[] = [];
  isPasswordMatch: boolean = true;
  displayErrorField: boolean = true;
  errorMessageMap = {
    firstName: {
      required: 'This field is required.',
      pattern: 'Only alphabets allowed',
      minlength: 'Minimum 1 characters required',
      maxlength: 'Maximum 50 characters accepted',
    },
    lastName: {
      required: 'This field is required.',
      pattern: 'Only alphabets allowed',
      minlength: 'Minimum 1 characters required',
      maxlength: 'Maximum 50 characters accepted'
    },
    dob: {
      required: 'This field is required.',
      invalidParticipantPastDob: 'Participant age should not be more than 18 years',
      invalidParticipantFutureDob: 'Today\'s and future date is not allowed',
      invalidUserPastDob: 'Age should be more than 18 years',
      invalidUserFutureDob: 'Future date is not allowed'
    },
    gender: {
      required: 'This field is required.'
    },
    email: {
      required: 'This field is required.',
      pattern: 'Invalid Email Id'
    },
    username: {
      required: 'This field is required.',
      minlength: 'Minimum 2 characters required',
      spaceCheckFailed: 'Spaces not allowed',
      specialCharCheckFailed: 'Only [._-] symbols allowed',
      digitsAtStartCheckFailed: 'Username cannot start with digits & [._-]'
    },
    password: {
      required: 'This field is required.',
      minlength: `Password must contain at least ${this.validation.password.length.min} characters`,
      maxlength: `Password must contain only ${this.validation.password.length.max} characters`,
      lowerCaseCheckFailed: 'Password must contain at least 1 Lowercase character',
      upperCaseCheckFailed: 'Password must contain at least 1 Uppercase character',
      numberCheckFailed: 'Password must contain at least 1 numeric character',
      specialCharCheckFailed: 'Password must contain a Special character',
      containsSpaceCheckFailed: 'Password Cannot start with or contain space in between',
    },
    confirmPassword: {
      required: 'This field is required.',
      passwordMatchFailed: 'Passwords do NOT match..'
    }
  };

  form = new FormGroup({
    firstName: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.user.firstName.regex),
        Validators.minLength(this.validation.user.firstName.length.min),
        Validators.maxLength(this.validation.user.firstName.length.max),
      ],
    }),
    lastName: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.user.lastName.regex),
        Validators.minLength(this.validation.user.lastName.length.min),
        Validators.maxLength(this.validation.user.lastName.length.max),
      ],
    }),
    dob: new FormControl({value: '', disabled: false}, [Validators.required, this.validateDateOfBirth.bind(this)]),
    gender: new FormControl({value: null, disabled: false}, [Validators.required]),
    email: new FormControl({value: '', disabled: true}, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.email.regex)
      ]
    }),
    username: new FormControl({value: '', disabled: false}, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.user.username.length.min),
        this.commonService.validateUsername
      ]
    }),
    password: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.password.length.min),
        Validators.maxLength(this.validation.password.length.max),
        this.commonService.validatePassword
      ],
    }),
    confirmPassword: new FormControl('', {
      validators: [
        Validators.required,
      ],
    })
  }, {validators: [this.validateConfirmPassword]});

  uuid: string;
  generatedUsername: string;
  inputUsername: string;
  isInitialLoad = true;

  constructor(
    private commonService: CommonService,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
  ) {
    this.route.queryParams.subscribe(params => {
      let {data} = params;
      try {
        if (params['token']) {
          this.commonService.setRegisterToken(params['token']);
        }

        data = JSON.parse(atob(params['data']));

        this.userRole = data?.role;
        this.uuid = data?.uuid;
        this.generatedUsername = data?.username;
        this.inputUsername = data?.username;

        this.form.patchValue({
          email: data?.email,
          username: data?.username,
          firstName: data?.firstName,
          lastName: data?.lastName,
          dob: data?.dob
        }, {emitEvent: false});
      }
      catch (e) {
        this.enableForm = true;
      }
    });

    this.store.dispatch(FetchGenderFilterRequest());

    this.store.select(genderInfo).pipe(takeUntil(this.onDestroy)).subscribe((data) => {
      this.dropdownOptions = data?.list;
    });

    // this.form.get('password').valueChanges.subscribe(() => {
    //   this.checkPasswordMatch();
    // });
    //
    // this.form.get('confirmPassword').valueChanges.subscribe(() => {
    //   this.checkPasswordMatch();
    // });
  }

  validateDateOfBirth(control: AbstractControl): ValidationErrors | null {
    const dob = control.value;
    if (this.userRole === roles['participant']) {
      if (dob < this.minDobDate) {
        return {invalidParticipantPastDob: true};
      }
      if (dob > this.maxDobDate) {
        return {invalidParticipantFutureDob: true};
      }
    } else {
      if (dob > this.maxDobDate && dob < moment()) {
        return {invalidUserPastDob: true};
      }
      if (dob > moment()) {
        return {invalidUserFutureDob: true};
      }
    }
    return null;
  }

  ngOnInit() {
    if (this.userRole === roles['participant']) {
      this.maxDobDate = moment().subtract(1, 'days');
      this.minDobDate = moment().subtract(18, 'year');
    } else {
      this.maxDobDate = moment().subtract(18, 'year');
    }

    this.form.controls['username'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(data => {
      this.inputUsername = data;
      this.usernameStatusLoading = false;
      this.displayMessage = false;
      if (data) {
        this.verifyUsername = true;
        this.enableForm = this.inputUsername === this.generatedUsername;
        this.isUsernameAvailable = this.inputUsername === this.generatedUsername;
      }

      if (data === '') {
        this.verifyUsername = false;
      }
    });

    this.store.select(isUsernameAvailable).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      if (!this.isInitialLoad) {
        this.isUsernameAvailable = data?.isUsernameAvailable;
        this.usernameStatusLoading = data?.loading;

        if (data.loading) {
          this.verificationStatusIcon = null;
        } else {
          if (this.isUsernameAvailable) {
            this.verificationStatusIcon = 'check-circle';
            this.enableForm = true;
          } else {
            this.verificationStatusIcon = 'exclamation-circle';
            this.enableForm = false;
          }
        }
        this.verifyUsername = false;
      }
    });

    this.store.select(registrationInfo).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      this.registerLoading = data?.loading;
    });

    this.form.controls.username.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(data => {
      this.verificationStatusIcon = '';
      this.verifyUsername = true;
    });

    this.isInitialLoad = false;
  }

  register() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      this.checkForErrors();
      setTimeout(() => {
        $(".form-field.ng-invalid")[0].parentElement.scrollIntoView();
      }, 0);
      return;
    }

    // EXTRACT VALUE FROM FORM//
    const {
      firstName,
      lastName,
      dob,
      gender,
      username,
      password,
      confirmPassword
    } = this.form.getRawValue();

    // PAYLOAD CREATION//
    const payload = {
      firstName,
      lastName,
      dob,
      gender,
      username,
      password,
      confirmPassword,
      avatarId: this.avatarId
    };
    // ACTION DISPATCH
    this.store.dispatch(RegisterRequest({payload}));
  }

  verify() {
    if (this.form?.controls?.['username']?.errors?.['spaceCheckFailed']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].spaceCheckFailed;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['username']?.errors?.['specialCharCheckFailed']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].specialCharCheckFailed;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['username']?.errors?.['minlength']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].minlength;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['username']?.errors?.['digitsAtStartCheckFailed']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].digitsAtStartCheckFailed;
      this.displayErrorField = true;
      return;
    }

    this.displayErrorField = false;

    const firstName = this.form.controls['firstName'].getRawValue();
    const lastName = this.form.controls['lastName'].getRawValue();
    const username = this.form.controls['username'].getRawValue();
    this.displayMessage = !!username.length;
    const payload = {
      firstName,
      lastName,
      username,
      uuid: this.uuid
    };
    this.store.dispatch(VerifyUsernameRequest({payload}));
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const passwordControl = control.get('password');
    const confirmPasswordControl = control.get('confirmPassword');

    if (passwordControl && confirmPasswordControl && passwordControl.value !== confirmPasswordControl.value) {
      confirmPasswordControl.setErrors({passwordMatchFailed: true});
    } else if (confirmPasswordControl.hasError('passwordMatchFailed')) {
      confirmPasswordControl.setErrors(null);
    }

    return null;
  }


  selectAvatar(avatar) {
    this.avatarId = avatar.id;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

}
